import { useLang } from "../i18n";
import { useSEO } from "../seo";
import { FadeIn, ParallaxImage } from "../components/Reveal";
import { CTAButton, PageHero, SectionLabel } from "../components/Bits";

const IMG = {
  hero: "https://images.unsplash.com/photo-1460574283810-2aab119d8511?q=80&w=2000&auto=format&fit=crop",
};

const copy = {
  it: {
    seoTitle: "General Contractor | Nicole Srl",
    seoDesc: "Un unico interlocutore per la ristrutturazione: Nicole Srl coordina imprese, fornitori, tecnici e tempi, dall'idea alla consegna.",
    kicker: "General Contractor",
    lines: ["Un unico interlocutore.", "Dall'idea alla consegna."],
    sub: "Gestire una ristrutturazione significa coordinare imprese, fornitori, tecnici, lavorazioni, materiali e tempi. Come General Contractor, Nicole Srl coordina il processo e le figure coinvolte, offrendo al cliente un unico riferimento durante l'intervento.",
    label: "Il servizio",
    title: "Un progetto coordinato dall'inizio alla fine.",
    intro: "In funzione del progetto, il servizio può comprendere:",
    items: [
      "Definizione preliminare dell'intervento",
      "Pianificazione",
      "Coordinamento delle imprese",
      "Gestione delle diverse lavorazioni",
      "Coordinamento fornitori",
      "Gestione delle tempistiche",
      "Controllo dell'avanzamento",
      "Gestione delle finiture",
      "Coordinamento con tecnici e professionisti incaricati",
      "Consegna finale dell'intervento",
    ],
    note: "Le attività riservate a professionisti abilitati — progettazione architettonica, direzione lavori, pratiche edilizie — restano affidate ai professionisti incaricati. Nicole Srl coordina le figure tecniche e professionali coinvolte, nell'interesse del cliente.",
    ctaTitle: "Un unico riferimento per il tuo intervento.",
    ctaText: "Raccontaci il progetto: valutiamo insieme percorso, tempi e priorità.",
    cta: "Richiedi una valutazione del progetto",
  },
  en: {
    seoTitle: "General Contractor | Nicole Srl",
    seoDesc: "A single point of contact for your renovation: Nicole Srl coordinates contractors, suppliers, technicians and timelines, from idea to delivery.",
    kicker: "General Contractor",
    lines: ["One point of contact.", "From idea to delivery."],
    sub: "Managing a renovation means coordinating contractors, suppliers, technicians, works, materials and timelines. As General Contractor, Nicole Srl coordinates the process and the parties involved, offering the client a single point of reference throughout the intervention.",
    label: "The service",
    title: "A project coordinated from start to finish.",
    intro: "Depending on the project, the service may include:",
    items: [
      "Preliminary definition of the intervention",
      "Planning",
      "Contractor coordination",
      "Management of the different works",
      "Supplier coordination",
      "Timeline management",
      "Progress monitoring",
      "Finishes management",
      "Coordination with appointed technicians and professionals",
      "Final delivery of the intervention",
    ],
    note: "Activities reserved to licensed professionals — architectural design, works supervision, building permits — remain with the appointed professionals. Nicole Srl coordinates the technical and professional figures involved, in the client's interest.",
    ctaTitle: "One point of reference for your project.",
    ctaText: "Tell us about the project: we assess the path, the timeline and the priorities together.",
    cta: "Request a project assessment",
  },
};

export default function GeneralContractor() {
  const { lang } = useLang();
  const t = copy[lang];
  useSEO({ title: t.seoTitle, description: t.seoDesc, path: "/general-contractor", lang });

  return (
    <div data-testid="general-contractor-page">
      <PageHero kicker={t.kicker} lines={t.lines} sub={t.sub} image={IMG.hero} imageAlt={lang === "it" ? "Architettura contemporanea — prospettiva" : "Contemporary architecture — perspective"} />

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
        <FadeIn>
          <SectionLabel>{t.label}</SectionLabel>
          <h2 className="mt-10 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight md:mt-14 md:text-5xl">
            {t.title}
          </h2>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-stone">{t.intro}</p>
        </FadeIn>
        <div className="mt-14 md:mt-20">
          {t.items.map((item, i) => (
            <FadeIn key={item} delay={i * 0.03}>
              <div className="group grid items-baseline gap-3 border-b border-ink/10 py-6 transition-colors duration-500 md:grid-cols-12 md:py-7">
                <span className="font-serif text-xl font-light text-ink/20 transition-colors duration-500 group-hover:text-bronze md:col-span-2">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-base leading-snug md:col-span-10">{item}</span>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.1}>
          <div className="mt-16 border-l-2 border-bronze bg-dove/40 p-8 md:p-10">
            <p className="max-w-3xl text-sm leading-relaxed">{t.note}</p>
          </div>
        </FadeIn>
      </section>

      <section className="bg-ink text-ivory">
        <div className="mx-auto max-w-7xl px-6 py-24 text-center md:px-10 md:py-36">
          <FadeIn>
            <h2 className="mx-auto max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight md:text-5xl">
              {t.ctaTitle}
            </h2>
            <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-ivory/70">{t.ctaText}</p>
            <div className="mt-10 flex justify-center">
              <CTAButton to="/contatti" variant="light" testId="gc-cta">
                {t.cta}
              </CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
